import React, { useState, useEffect, useImperativeHandle, forwardRef, useMemo } from 'react';
import { IonModal } from '@ionic/react';
import { TAKEOUT_TYPES } from '../../../global/request/bet';
import TakerError from './TakerError';
import FinalizeFight from './steps/FinalizeFight';
import ModifyMenu from './steps/ModifyMenu';
import PaymentStep from './steps/PaymentStep';
import '../../../styles/fight/takerProcess/TakerFightProcess.scss';

const TakerFightProcess = forwardRef(
  ({ fightData, optionalTakerVenue, onClose, notificationClose, onAcceptFight }, ref) => {
    const [isOpen, setIsOpen] = useState(false);
    const [step, setStep] = useState(0);
    const [menuSelections, setMenuSelections] = useState(null);
    const [takerTotalPrice, setTakerTotalPrice] = useState();
    const [visitedMenu, setVisitedMenu] = useState(true);
    const [paymentIntentId, setPaymentIntentId] = useState('');
    const [changedAddress, setChangedAddress] = useState('');
    const [changedAddressLatitude, setChangedAddressLatitude] = useState(0);
    const [changedAddressLongitude, setChangedAddressLongitude] = useState(0);
    const [changedAddressIsAutocompleted, setChangedAddressIsAutocompleted] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [showError, setShowError] = useState(false);
    const [animationDirection, setAnimationDirection] = useState('right');
    const [isAnimating, setIsAnimating] = useState(false);
    const [editItems, setEditItems] = useState(false);

    const makerTotalPrice = useMemo(() => {
      return fightData?.restaurantItems.reduce((sum, item) => sum + item.price * item.quantity, 0) || 0;
    }, [fightData?.restaurantItems]);

    useEffect(() => {
      setTakerTotalPrice(makerTotalPrice);
    }, [makerTotalPrice]);

    // eslint-disable-next-line no-unused-vars
    const [paymentInfo, setPaymentInfo] = useState({
      cardNumber: '',
      expiryDate: '',
      cvc: '',
      zip: '',
      country: 'United States',
      email: '',
      phone: '',
      saveInfo: false,
    });

    const handlePaymentUpdate = newPaymentData => {
      setPaymentInfo(prev => ({ ...prev, ...newPaymentData }));
    };

    useImperativeHandle(ref, () => ({
      present: () => {
        setIsOpen(true);
        setVisitedMenu(true);
      },
      dismiss: () => {
        setIsOpen(false);
        setStep(0);
        notificationClose?.();
      },
      skipToSummary: () => {
        if (!menuSelections || menuSelections.length === 0) {
          const initialSelections =
            fightData?.restaurantItems?.map(item => ({
              id: item.menu_item_id || item.product_id || item.id,
              name: item.name,
              price: item.price,
              amount: item.quantity || 1,
            })) || [];

          setMenuSelections(initialSelections);

          const initialTotal = initialSelections.reduce((sum, item) => sum + item.price * item.amount, 0);
          setTakerTotalPrice(initialTotal);
        }
        setIsOpen(true);
        setStep(1);
        setVisitedMenu(false);
      },
    }));

    const showErrorPopup = message => {
      setErrorMessage(message);
      setShowError(true);
    };

    const handlePaymentConfirm = receivedPaymentIntentId => {
      setPaymentIntentId(receivedPaymentIntentId);

      if (onAcceptFight) {
        const formattedTakerItems = menuSelections
          ? menuSelections.map(item => {
              return {
                menu_item_id: typeof item.id === 'number' ? item.id : -1,
                product_id: typeof item.id === 'string' ? item.id : '',
                quantity: item.amount ?? 1,
                price: item.price ?? 0,
                name: item.name ?? 'Unknown Item',
              };
            })
          : [];

        const deliveryCoords = {
          lat: changedAddressLatitude,
          lng: changedAddressLongitude,
          isReliable: changedAddressIsAutocompleted,
        };

        onAcceptFight(
          fightData.id,
          receivedPaymentIntentId,
          changedAddress || '',
          formattedTakerItems,
          '',
          deliveryCoords.lat,
          deliveryCoords.lng,
        )
          .then(() => {
            setIsOpen(false);
            setStep(0);
            notificationClose?.();
          })
          .catch(error => {
            console.error('[TakerFightProcess] Error accepting fight after payment:', error);
            showErrorPopup('Failed to accept fight. Please try again.');
          });
      }
    };

    const handleNextStep = () => {
      if (step === 0) {
        if (takerTotalPrice > makerTotalPrice) {
          showErrorPopup(`Please select items less than $${makerTotalPrice.toFixed(2)}`);
          return;
        }
      }
      if (step < 2) {
        setAnimationDirection('right');
        setIsAnimating(true);
        setTimeout(() => {
          setStep(step + 1);
          setIsAnimating(false);
        }, 125);
      } else {
        setIsOpen(false);
      }
    };

    const handlePreviousStep = () => {
      if (step > 0) {
        setAnimationDirection('left');
        setIsAnimating(true);
        setTimeout(() => {
          setStep(step - 1);
          setIsAnimating(false);
        }, 125);
      }
    };

    const getAnimationClass = () => {
      if (step === 0) {
        return '';
      }
      if (isAnimating) {
        return `slide-${animationDirection === 'right' ? 'out-left' : 'out-right'}`;
      }
      return `slide-${animationDirection === 'right' ? 'in-right' : 'in-left'}`;
    };

    const onCompleted = () => {
      setIsOpen(false);
      if (!paymentIntentId && makerTotalPrice !== 0 && (takerTotalPrice !== 0 || takerTotalPrice !== undefined)) {
        showErrorPopup('Please add a payment method.');
      }
    };

    const onMenuNext = (newMenuSelections, newTotalPrice) => {
      setTakerTotalPrice(newTotalPrice);
      setMenuSelections(newMenuSelections);
      handleNextStep();
    };

    const summarySaveMenuChanges = (newMenuSelections, newTotalPrice) => {
      setTakerTotalPrice(newTotalPrice);
      setMenuSelections(newMenuSelections);
    };

    const onEditItems = () => {
      setEditItems(true);
      handlePreviousStep();
    };

    const saveAddressChange = (newAddress, newAddressLatitude, newAddressLongitude, newIsAddressAutocompleted) => {
      setChangedAddress(newAddress);
      setChangedAddressLatitude(newAddressLatitude);
      setChangedAddressLongitude(newAddressLongitude);
      setChangedAddressIsAutocompleted(newIsAddressAutocompleted);
    };

    const onCloseModal = () => {
      setIsOpen(false);
    };

    return (
      <>
        <IonModal
          isOpen={isOpen}
          onDidDismiss={() => {
            setIsOpen(false);
            setStep(0);
            onClose?.();
          }}
          className='taker-fight-process'
          initialBreakpoint={1}
          breakpoints={[0, 1]}>
          {step === 0 && (
            <ModifyMenu
              fightData={fightData}
              optionalTakerVenue={optionalTakerVenue}
              startingSelections={menuSelections}
              onNext={onMenuNext}
              onCloseModal={onCloseModal}
            />
          )}

          {step === 1 && (
            <FinalizeFight
              fightData={fightData}
              menuSelections={menuSelections}
              takerTotalPrice={takerTotalPrice}
              makerTotalPrice={makerTotalPrice}
              visitedMenu={visitedMenu}
              closeModal={onCloseModal}
              notificationClose={notificationClose}
              saveMenuChanges={summarySaveMenuChanges}
              goToModifyMenu={onEditItems}
              editItems={editItems}
              onNext={handleNextStep}
              onComplete={onCompleted}
              paymentId={paymentIntentId}
              changedAddress={changedAddress}
              changedAddressLatitude={changedAddressLatitude}
              changedAddressLongitude={changedAddressLongitude}
              changedAddressIsAutocompleted={changedAddressIsAutocompleted}
              saveAddressChange={saveAddressChange}
              showErrorPopup={showErrorPopup}
              optionalTakerVenue={optionalTakerVenue}
              onAcceptFight={onAcceptFight}
              className={getAnimationClass()}
            />
          )}

          {step === 2 && (
            <PaymentStep
              onNext={handlePaymentConfirm}
              onBack={handlePreviousStep}
              onPaymentUpdate={handlePaymentUpdate}
              makerTotalPrice={makerTotalPrice}
              onCloseModal={onCloseModal}
              isDelivery={fightData.takeoutType === TAKEOUT_TYPES.DELIVERY}
              className={getAnimationClass()}
            />
          )}
        </IonModal>
        <TakerError
          isOpen={showError}
          onClose={() => setShowError(false)}
          setIsOpen={setShowError}
          msg={errorMessage}
        />
      </>
    );
  },
);

TakerFightProcess.displayName = 'TakerFightProcess';

export default TakerFightProcess;
